import React from 'react';
import { sanitizeHTML } from '../utils/validation';

interface SafeHTMLProps {
  content: string;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

/**
 * Component for safely rendering user-generated content
 * Prevents XSS attacks by sanitizing HTML content
 */
export const SafeHTML: React.FC<SafeHTMLProps> = ({ 
  content, 
  className,
  as: Component = 'div' 
}) => {
  // Sanitize the content to prevent XSS
  const safeContent = sanitizeHTML(content);
  
  // For plain text content, we can safely use dangerouslySetInnerHTML
  // since we've sanitized it
  return (
    <Component 
      className={className}
      dangerouslySetInnerHTML={{ __html: safeContent }}
    />
  );
};

interface SafeTextProps {
  children: string;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

/**
 * Component for safely rendering plain text
 * Automatically escapes any HTML entities
 */
export const SafeText: React.FC<SafeTextProps> = ({ 
  children, 
  className,
  as: Component = 'span' 
}) => {
  return (
    <Component className={className}>
      {children}
    </Component>
  );
};

interface SafeUserContentProps {
  content: string;
  maxLength?: number;
  className?: string;
  showMore?: boolean;
}

/**
 * Component for rendering user-generated content with additional features
 * like truncation and "show more" functionality
 */
export const SafeUserContent: React.FC<SafeUserContentProps> = ({ 
  content, 
  maxLength = 300,
  className,
  showMore = true 
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  // Sanitize content
  const safeContent = sanitizeHTML(content);
  
  // Check if content needs truncation
  const needsTruncation = content.length > maxLength && !isExpanded;
  const displayContent = needsTruncation 
    ? safeContent.substring(0, maxLength) + '...' 
    : safeContent;
  
  return (
    <div className={className}>
      <div dangerouslySetInnerHTML={{ __html: displayContent }} />
      {showMore && content.length > maxLength && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="show-more-button"
          aria-expanded={isExpanded}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

// Hook for sanitizing content
export const useSanitize = () => {
  return React.useCallback((content: string) => {
    return sanitizeHTML(content);
  }, []);
};